import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Clock, Mail, ArrowRight } from 'lucide-react';
import { APP_CONFIG } from '../../config/constants';

const BookingConfirmed = ({ onDone }) => {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    const processBooking = async () => {
      try {
        // Get URL parameters
        const params = new URLSearchParams(window.location.search);

        // Extract booking data from URL
        const leadId = params.get('leadId');
        const name = decodeURIComponent(params.get('name') || '');
        const email = decodeURIComponent(params.get('email') || '');

        // Calendly redirect parameters (may or may not be present depending on plan)
        const startTime = params.get('event_start_time') || params.get('startTime');
        const endTime = params.get('event_end_time') || params.get('endTime');
        const eventUri = params.get('event_uri');

        console.log('📋 Booking redirect params:', {
          leadId,
          name,
          email,
          startTime,
          endTime,
          eventUri
        });

        if (!leadId) {
          console.error('❌ No leadId in URL');
          setStatus('error');
          return;
        }

        // Prepare booking data
        const bookingData = {
          leadId,
          eventUri: eventUri || 'https://calendly.com/booking',
          startTime: startTime || null,
          endTime: endTime || null,
          inviteeName: name,
          inviteeEmail: email,
        };

        // If no times from Calendly, use placeholder (free plan limitation)
        if (!startTime || !endTime) {
          console.log('⚠️ No time from Calendly (free plan). Using placeholder.');
          console.log('⚠️ Check Calendly dashboard for actual booking time.');
        }

        // Send to backend
        if (APP_CONFIG.API_BASE_URL && !APP_CONFIG.API_BASE_URL.includes('your-backend')) {
          try {
            const response = await fetch(`${APP_CONFIG.API_BASE_URL}/leads/bookings`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(bookingData),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to save booking');
            }

            const result = await response.json();
            console.log('✅ Booking saved:', result);
          } catch (apiError) {
            console.error('API Error:', apiError);
            // Continue to show success - booking was created in Calendly
          }
        }

        setBookingDetails({
          name,
          email,
          startTime,
          endTime
        });
        setStatus('success');

        // Clean URL params after processing
        window.history.replaceState({}, document.title, window.location.pathname);

      } catch (error) {
        console.error('❌ Error processing booking:', error);
        setStatus('error');
      }
    };

    processBooking();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Check your email for details';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Check your email for details';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-950/95 backdrop-blur-md"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 25 }}
        className="w-full max-w-md"
      >
        <div className="glass rounded-3xl overflow-hidden">
          {/* Glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-transparent to-transparent pointer-events-none" />

          <div className="relative p-8 text-center">
            {status === 'processing' && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Confirming Booking</h2>
                <p className="text-white/60">Please wait while we confirm your demo...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Demo Scheduled!</h2>
                <p className="text-white/60 mb-6">
                  Thank you{bookingDetails?.name ? `, ${bookingDetails.name.split(' ')[0]}` : ''}! Your demo has been scheduled.
                </p>

                {bookingDetails?.startTime && (
                  <div className="glass rounded-xl p-4 mb-6 text-left">
                    <div className="flex items-center gap-3 mb-3">
                      <Calendar className="w-5 h-5 text-blue-400" />
                      <span className="text-white/80">{formatDate(bookingDetails.startTime)}</span>
                    </div>
                    {bookingDetails.endTime && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-blue-400" />
                        <span className="text-white/80">
                          {formatTime(bookingDetails.startTime)} - {formatTime(bookingDetails.endTime)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {!bookingDetails?.startTime && (
                  <div className="glass rounded-xl p-4 mb-6 text-left">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-400" />
                      <span className="text-white/80 text-sm">
                        Check your email for the confirmed date & time
                      </span>
                    </div>
                  </div>
                )}

                <button
                  onClick={onDone}
                  className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Something went wrong</h2>
                <p className="text-white/60 mb-6">
                  Your demo was booked in Calendly. Please check your email for confirmation.
                </p>
                <button
                  onClick={onDone}
                  className="w-full py-3 px-6 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BookingConfirmed;